import React, { useMemo } from 'react';

// Delivery zone preview map. Uses the Mapbox STATIC IMAGES API (a plain
// <img>) instead of mapbox-gl on purpose: CRA's production minifier breaks
// mapbox-gl's bundled parser ("ReferenceError: o is not defined" inside
// setPaintProperty — dev builds are unminified, so it only ever failed in
// prod). A static image has zero JS surface to break, and for a zone
// preview we don't need pan/zoom.
//
// Renders: restaurant marker + the delivery-radius circle (fill + outline),
// auto-fitted to the circle.

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
const EARTH_RADIUS_MILES = 3958.8;

function circlePolygon(lng, lat, radiusMiles, steps = 48) {
  const coords = [];
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    const dy = radiusMiles * Math.sin(angle);
    const dx = radiusMiles * Math.cos(angle);
    const dLat = (dy / EARTH_RADIUS_MILES) * (180 / Math.PI);
    const dLng = (dx / (EARTH_RADIUS_MILES * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);
    coords.push([lng + dLng, lat + dLat]);
  }
  return coords;
}

const DeliveryZoneMap = ({ latitude, longitude, radiusMiles, height = 260 }) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  const radius = Number(radiusMiles) > 0 ? Number(radiusMiles) : 5;
  const hasCoords = isFinite(lat) && isFinite(lng) && (lat !== 0 || lng !== 0);

  const imageUrl = useMemo(() => {
    if (!hasCoords || !MAPBOX_ACCESS_TOKEN) return null;

    // GeoJSON overlay: the radius circle (polygon) + the restaurant marker.
    const geojson = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {
            fill: '#FF5722',
            'fill-opacity': 0.15,
            stroke: '#FF5722',
            'stroke-width': 2,
          },
          geometry: { type: 'Polygon', coordinates: [circlePolygon(lng, lat, radius)] },
        },
        {
          type: 'Feature',
          properties: { 'marker-color': '#FF5722', 'marker-size': 'medium' },
          geometry: { type: 'Point', coordinates: [lng, lat] },
        },
      ],
    };
    const overlay = encodeURIComponent(JSON.stringify(geojson));
    // `auto` viewport = the API fits the map to the overlay (the circle).
    return `https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/geojson(${overlay})/auto/800x520@2x?access_token=${MAPBOX_ACCESS_TOKEN}&padding=40`;
  }, [lat, lng, radius, hasCoords]);

  if (!hasCoords) {
    return (
      <div
        className="d-flex align-items-center justify-content-center text-muted"
        style={{ height, background: '#eef1f4', borderRadius: 12, border: '1px dashed #cdd3da', fontSize: '0.85rem' }}
      >
        <span className="text-center px-3">
          <i className="fas fa-map-marked-alt d-block mb-1" style={{ fontSize: '1.4rem' }}></i>
          Save an address with coordinates above to preview the delivery zone map.
        </span>
      </div>
    );
  }

  return (
    <div style={{ height, width: '100%', borderRadius: 12, overflow: 'hidden', border: '1px solid #e3e6ea', background: '#eef1f4' }}>
      {imageUrl && (
        <img
          src={imageUrl}
          alt={`Delivery zone: ${radius} miles around the restaurant`}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}
    </div>
  );
};

export default DeliveryZoneMap;
